import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { AuthService } from "apis/Services/AuthService";
import AuthWrapper from "components/auth-page-wrapper";
import ButtonComponent from "components/button-component";
import InputComponent from "components/input-component";
import React, { useEffect } from "react";
import Text from "components/text";
import styles from "./login.module.css";
import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { AuthStorage } from "utils/auth-storage";

function getRememberedCompanyId() {
  const storedCompanyId = AuthStorage.getItem("company_id");

  if (storedCompanyId) {
    return storedCompanyId;
  }

  try {
    const companies = JSON.parse(AuthStorage.getItem("companies_object") || "{}");
    const companyIds = Object.values(companies || {})
      .map((companyId) => String(companyId || "").trim())
      .filter(Boolean);
    const cachedCompanyId = companyIds.find((companyId) =>
      localStorage.getItem(`projects_${companyId}`),
    );

    return cachedCompanyId || companyIds[0] || "";
  } catch (error) {
    console.error("Failed to read remembered companies", error);
    return "";
  }
}

const Login = () => {
  const navigate = useNavigate();
  const [loginError, setloginError] = useState(null);
  const [showPassword, setshowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  let schema = yup.object().shape({
    loginEmail: yup
      .string()
      .required("This field is required")
      .email("Please enter a valid email"),
    loginPassword: yup.string().required("This field is required"),
  });

  useEffect(() => {
    const restoreRememberedSession = async () => {
      if (!AuthStorage.shouldRemember()) {
        return;
      }

      const loginEmail = AuthStorage.getItem("email");
      const token = AuthStorage.getItem("token");
      const refreshToken = AuthStorage.getItem("refreshToken");
      const companyId = getRememberedCompanyId();

      if (!loginEmail || !token || !refreshToken || !companyId) {
        return;
      }

      AuthStorage.setItem("company_id", companyId);
      setLoading(true);
      try {
        const res = await AuthService.refreshToken({
          loginEmail,
          access_token: token,
          refresh_token: refreshToken,
          company_id: companyId,
        });

        AuthStorage.setItem("token", res.access_token);
        AuthStorage.setItem("refreshToken", res.refresh_token);
        AuthStorage.setItem("lastRequestTime", new Date().getTime());

        navigate(`/main/company/${companyId}/projects`, { replace: true });
      } catch (err) {
        AuthStorage.clearAuthData();
        setLoading(false);
      }
    };

    restoreRememberedSession();
  }, [navigate]);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      loginEmail: window.localStorage.getItem("email") || "",
      loginPassword: "",
      remember: AuthStorage.shouldRemember(),
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = async (v) => {
    await AuthService.post_login(v)
      .then((res) => {
        setLoading(true);
        AuthStorage.prepareLogin(Boolean(v.remember));
        window.location.replace(res.url);
      })
      .catch((err) => {
        console.log(err, "err");
        setloginError(true);
        setLoading(false);
      });
  };

  const switchShowPassword = () => {
    setshowPassword(!showPassword);
  };

  return (
    <AuthWrapper>
      {/* <Spin indicator={indicator} spinning={loader}> */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Text fontSize={38} className="mb-4 fw-bold text-wrap">
          Welcome to Koncurent
        </Text>
        <div className="d-flex flex-column mt-3">
          <Controller
            name="loginEmail"
            control={control}
            render={({ field }) => (
              <InputComponent
                label="Email"
                errors={errors.loginEmail}
                placeholder="Enter your email"
                {...field}
              />
            )}
          />
          {errors?.loginEmail && (
            <Text className={styles.errorText}>
              {errors.loginEmail.message}
            </Text>
          )}
          <div className="d-flex align-items-center">
            <div className="flex-grow-1">
              <Controller
                name="loginPassword"
                control={control}
                render={({ field }) => (
                  <InputComponent
                    label="Password"
                    errors={errors.loginPassword}
                    className={"my-2"}
                    placeholder="Enter your password"
                    type={showPassword ? "text" : "password"}
                    {...field}
                  />
                )}
              />
            </div>
            <div className="pointer p-3 pt-4 mt-3" onClick={switchShowPassword}>
              {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </div>
          </div>
          {errors?.loginPassword && (
            <Text className={styles.errorText}>
              {errors.loginPassword.message}
            </Text>
          )}

          <div className="d-flex justify-content-between w-100 flex-wrap flex-md-nowrap my-2 ">
            <div className="d-flex align-items-center">
              <Controller
                name="remember"
                control={control}
                render={({ field }) => (
                  <InputComponent
                    type="checkbox"
                    className="checkbox"
                    {...field}
                  />
                )}
              />
              <Text className={`${styles.remember} mx-1`}>Remember Me</Text>
            </div>
            <div className="d-flex  gap-1">
              <Text className={`${styles.forgetPass}`}>
                Forgot your password
              </Text>
              <Text
                className={`${styles.link} pointer`}
                onClickAction={() => navigate("/forget-password")}
              >
                Click here.
              </Text>
            </div>
          </div>
          {loginError && (
            <Text className={styles.errorText}>
              You have entered an invalid username or password
            </Text>
          )}
          <ButtonComponent
            isLoading={loading}
            title={"Login"}
            className={`mt-3`}
            disabled={loading}
          />
        </div>
      </form>
      {/* </Spin> */}
    </AuthWrapper>
  );
};

export default Login;
